import { Box, Stack, useTheme } from "@mui/material";
import { SearchBox } from "@foodpilot/foods";
import type { ReactNode } from "react";
import { useResponsiveBreakpoints, useResponsivePadding } from "src/utils/useResponsiveBreakpoints";

type EntityListSearch = {
    value: string | null;
    onChange: (value: string) => void;
    placeholder?: string;
    showOnDesktop?: boolean;
};

type EntityListLayoutProps = {
    appearance: "legacy" | "hub";
    heading?: ReactNode;
    ariaLabel: string;
    search?: EntityListSearch;
    actions?: ReactNode;
    mobileActions?: ReactNode;
    beforeGrid?: ReactNode;
    gridHeader?: ReactNode;
    afterGrid?: ReactNode;
    hideHeadingOnMobile?: boolean;
    mobileHeading?: ReactNode;
    children: ReactNode;
};

export const EntityListLayout = (props: EntityListLayoutProps) => {
    const {
        actions,
        appearance,
        afterGrid,
        ariaLabel,
        beforeGrid,
        children,
        gridHeader,
        heading,
        hideHeadingOnMobile = false,
        mobileActions,
        mobileHeading,
        search,
    } = props;
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();
    const responsivePadding = useResponsivePadding();
    const isHub = appearance === "hub";
    const hasToolbarContent = heading !== undefined || actions !== undefined || search?.showOnDesktop === true;
    const showHeading = hasToolbarContent && (!isMobile || !hideHeadingOnMobile);
    const showMobileControls = isMobile && (search !== undefined || mobileActions !== undefined);

    return (
        <Box
            data-testid="entity-list-layout"
            sx={{
                ...(isHub ?
                    {
                        paddingBottom: isMobile ? theme.spacing(1) : theme.spacing(6),
                        paddingTop: hasToolbarContent ? undefined : theme.spacing(3),
                        paddingLeft: isMobile ? theme.spacing(1) : theme.spacing(6),
                        paddingRight: isMobile ? theme.spacing(1) : theme.spacing(6),
                    }
                :   { padding: responsivePadding }),
            }}
        >
            {showHeading && (
                <Stack
                    data-testid="entity-list-toolbar"
                    direction={isMobile ? "column" : "row"}
                    useFlexGap
                    alignItems={isMobile ? "stretch" : "center"}
                    justifyContent="space-between"
                    gap={theme.spacing(2)}
                    sx={
                        isHub ?
                            { paddingBottom: theme.spacing(3), paddingTop: theme.spacing(3) }
                        :   { marginBottom: theme.spacing(5) }
                    }
                >
                    {heading}
                    {!isMobile && (search?.showOnDesktop || actions) && (
                        <Stack
                            data-testid="entity-list-desktop-controls"
                            direction="row"
                            useFlexGap
                            alignItems="center"
                            gap={isHub ? theme.spacing(1) : theme.spacing(1.5)}
                        >
                            {search?.showOnDesktop && (
                                <Box sx={{ flexShrink: 0, width: theme.spacing(40) }}>
                                    <SearchBox
                                        variant="standalone"
                                        value={search.value}
                                        placeholder={search.placeholder}
                                        onChange={search.onChange}
                                    />
                                </Box>
                            )}
                            {actions}
                        </Stack>
                    )}
                </Stack>
            )}

            {isMobile && mobileHeading}

            {showMobileControls && (
                <Stack
                    data-testid="entity-list-mobile-controls"
                    useFlexGap
                    gap={theme.spacing(1.5)}
                    sx={{
                        marginBottom: theme.spacing(2.5),
                        marginTop: hideHeadingOnMobile ? theme.spacing(8) : 0,
                        width: "100%",
                        "& > *": { width: "100%" },
                    }}
                >
                    {search && (
                        <SearchBox
                            variant="standalone"
                            value={search.value}
                            placeholder={search.placeholder}
                            onChange={search.onChange}
                        />
                    )}
                    {mobileActions}
                </Stack>
            )}

            {beforeGrid}

            <Box
                component="section"
                role="region"
                aria-label={ariaLabel}
                sx={{
                    backgroundColor: theme.custom.grey[100],
                    border: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                    borderRadius: theme.spacing(2),
                    boxShadow: isHub ? theme.custom.shadows.grey : "none",
                    minWidth: 0,
                    overflow: isHub ? "hidden" : "visible",
                    marginTop: !isHub && isMobile ? theme.spacing(0.5) : 0,
                }}
            >
                {gridHeader}
                {children}
            </Box>

            {afterGrid}
        </Box>
    );
};
